import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  FormControl, 
  GridLegacy as Grid, 
  InputLabel, 
  MenuItem, 
  Select, 
  TextField 
} from '@mui/material';
import { Content, ContentType } from '../../types/content';
import { rtlTextFieldProps } from '../../utils/rtl';

interface ContentFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: { key: string; title: string; content: string; type: ContentType }) => Promise<void>;
  initialData?: Content | null;
  title: string;
}

const ContentFormModal: React.FC<ContentFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  title
}) => {
  const [key, setKey] = useState<string>('');
  const [contentTitle, setContentTitle] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [type, setType] = useState<ContentType>(ContentType.WELCOME);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setKey(initialData.key);
      setContentTitle(initialData.title || '');
      setContent(initialData.content);
      setType(initialData.type);
    } else {
      resetForm();
    }
  }, [initialData, open]);

  const resetForm = () => {
    setKey('');
    setContentTitle('');
    setContent('');
    setType(ContentType.WELCOME);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!key.trim() || !content.trim()) {
      setError('כל השדות נדרשים');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSubmit({ key, title: contentTitle, content, type });
      handleClose();
    } catch (err) {
      console.error('Failed to save content:', err);
      setError('נכשל בשמירת התוכן');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      dir="rtl"
    >
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                id="key"
                label="מפתח"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                required
                variant="outlined"
                margin="normal"
                error={!!error && !key.trim()}
                helperText={!!error && !key.trim() ? 'נדרש מפתח' : ''}
                InputProps={{
                  sx: { textAlign: 'right' }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="type-label">סוג</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  value={type}
                  label="סוג"
                  onChange={(e) => setType(e.target.value as ContentType)}
                  required
                >
                  {Object.values(ContentType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="title"
                label="כותרת (עברית)"
                value={contentTitle}
                onChange={(e) => setContentTitle(e.target.value)}
                variant="outlined"
                margin="normal"
                InputProps={{
                  sx: { textAlign: 'right' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="content"
                label="תוכן"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                multiline
                rows={15}
                variant="outlined"
                margin="normal"
                error={!!error && !content.trim()}
                helperText={!!error && !content.trim() ? 'נדרש תוכן' : ''}
                {...rtlTextFieldProps}
              />
            </Grid>
          </Grid>
          
          {error && !error.includes('required') && (
            <div style={{ color: 'red', marginTop: '16px', textAlign: 'right' }}>{error}</div>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', direction: 'rtl', p: 2 }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? 'שומר...' : initialData ? 'עדכון' : 'יצירה'}
          </Button>
          <Button onClick={handleClose} color="secondary">
            ביטול
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ContentFormModal; 